// ExternalResourcesPanel — admin-only registry of MEGA share links for the
// bot's hybrid map-finding context. URLs never reach public users; the AI
// uses them silently to ground DAMOS/A2L/PRG/XDF answers.
import React, { useState, useEffect, useCallback } from 'react';
import { Library, Plus, Trash2, ExternalLink, Loader2, Database } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_LABELS = {
  damos: { label: 'DAMOS',  color: 'text-purple-400 border-purple-500/40 bg-purple-500/10' },
  a2l:   { label: 'A2L',    color: 'text-cyan-400 border-cyan-500/40 bg-cyan-500/10' },
  prg:   { label: 'PRG',    color: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' },
  xdf:   { label: 'XDF',    color: 'text-amber-400 border-amber-500/40 bg-amber-500/10' },
  bin:   { label: 'BIN',    color: 'text-orange-400 border-orange-500/40 bg-orange-500/10' },
  manual:{ label: 'Manual', color: 'text-zinc-300 border-zinc-500/40 bg-zinc-500/10' },
  tsb:   { label: 'TSB',    color: 'text-pink-400 border-pink-500/40 bg-pink-500/10' },
  wiring:{ label: 'Wiring', color: 'text-blue-400 border-blue-500/40 bg-blue-500/10' },
  other: { label: 'Other',  color: 'text-zinc-400 border-zinc-500/40 bg-zinc-500/10' },
};

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ExternalResourcesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState({ title: '', url: '', kind: 'damos', platform: '', ecu: '', notes: '' });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/resources`, { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const submit = async () => {
    if (!draft.title.trim() || !draft.url.trim()) {
      setError('Title and URL are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/resources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(draft),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 120)}`);
      }
      setDraft({ title: '', url: '', kind: 'damos', platform: '', ecu: '', notes: '' });
      setShowForm(false);
      fetchItems();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this resource?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/resources/${id}`, {
        method: 'DELETE',
        headers: { ...authHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchItems();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="bg-[#111111] border border-[#27272A] rounded-sm p-5" data-testid="external-resources-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Library className="w-5 h-5 text-[#FF5E1A]" />
          <h3 className="font-['Outfit'] text-lg font-black text-white">Bot Reference Library</h3>
          <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-500/40 bg-zinc-500/10 text-zinc-400 rounded-sm">
            admin-only · bot-internal
          </span>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="resources-toggle-form"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-[10px] font-bold tracking-wider uppercase rounded-sm transition-colors"
        >
          <Plus className="w-3 h-3" /> {showForm ? 'Cancel' : 'Add resource'}
        </button>
      </div>

      <p className="text-[11px] text-zinc-500 mb-4 leading-relaxed">
        DAMOS / A2L / PRG / XDF MEGA share links. The bot reads these silently when answering map-finding
        questions for matching ECUs. URLs are <span className="text-amber-400">never returned to public users</span>.
      </p>

      {error && (
        <div className="mb-3 p-2 border border-red-500/40 bg-red-500/10 rounded-sm text-xs text-red-300">{error}</div>
      )}

      {showForm && (
        <div className="mb-4 p-4 border border-[#27272A] bg-[#0A0A0A] rounded-sm space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title *" testId="resource-title-input">
              <input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="80B37E0E A2L (full)"
                className="w-full bg-[#0F0F0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white"
              />
            </Field>
            <Field label="MEGA URL *" testId="resource-url-input">
              <input
                value={draft.url}
                onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                placeholder="https://mega.nz/file/..."
                className="w-full bg-[#0F0F0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white font-['JetBrains_Mono']"
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Kind" testId="resource-kind-input">
              <select
                value={draft.kind}
                onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                className="w-full bg-[#0F0F0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white"
              >
                {Object.keys(KIND_LABELS).map((k) => (
                  <option key={k} value={k}>{KIND_LABELS[k].label}</option>
                ))}
              </select>
            </Field>
            <Field label="Platform tag" hint="e.g. bmw_msv80, gm_e38, megasquirt_ms3" testId="resource-platform-input">
              <input
                value={draft.platform}
                onChange={(e) => setDraft({ ...draft, platform: e.target.value })}
                placeholder="bmw_msv80"
                className="w-full bg-[#0F0F0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white font-['JetBrains_Mono']"
              />
            </Field>
            <Field label="ECU code" hint="e.g. 80B37E0E, MSV80" testId="resource-ecu-input">
              <input
                value={draft.ecu}
                onChange={(e) => setDraft({ ...draft, ecu: e.target.value })}
                placeholder="80B37E0E"
                className="w-full bg-[#0F0F0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white font-['JetBrains_Mono']"
              />
            </Field>
          </div>
          <Field label="Notes" testId="resource-notes-input">
            <textarea
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="What the bot should remember about this file (e.g. 'full A2L for hybrid map finding, contains KFMIOP/KFKHFM')"
              rows={2}
              className="w-full bg-[#0F0F0F] border border-[#27272A] rounded-sm px-3 py-2 text-xs text-white"
            />
          </Field>
          <div className="flex justify-end">
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="resource-submit-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF5E1A] hover:bg-[#FF7A42] disabled:opacity-50 text-white text-[10px] font-bold tracking-wider uppercase rounded-sm transition-colors"
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Database className="w-3 h-3" />}
              Save resource
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {loading && items.length === 0 && (
          <div className="flex items-center gap-2 text-zinc-500 text-xs"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-xs text-zinc-500 italic py-3">No resources yet. Add your first MEGA link above.</div>
        )}
        {items.map((r) => {
          const kind = KIND_LABELS[r.kind] || KIND_LABELS.other;
          return (
            <div key={r.id} className="border border-[#27272A] bg-[#0A0A0A] rounded-sm p-3 group" data-testid={`resource-row-${r.id}`}>
              <div className="flex items-start gap-3">
                <span className={`text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border whitespace-nowrap ${kind.color}`}>
                  {kind.label}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-bold truncate">{r.title}</div>
                  <div className="text-[10px] font-['JetBrains_Mono'] text-zinc-500 truncate">
                    {[r.platform, r.ecu].filter(Boolean).join(' · ') || 'untagged'}
                  </div>
                  {r.notes && <div className="text-[11px] text-zinc-400 mt-1 line-clamp-2">{r.notes}</div>}
                </div>
                <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    title="Open MEGA link in new tab"
                    className="p-1.5 text-zinc-400 hover:text-[#FF5E1A] transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <button
                    onClick={() => remove(r.id)}
                    title="Remove"
                    data-testid={`resource-delete-${r.id}`}
                    className="p-1.5 text-zinc-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, hint, testId, children }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.16em] text-zinc-500 block mb-1">
        {label}{hint && <span className="text-zinc-600 normal-case tracking-normal ml-2">— {hint}</span>}
      </span>
      {children}
    </label>
  );
}
